import { NavLink, Navigate, useParams, useSearchParams } from "react-router";
import { useProject } from "../api/queries";
import { ErrorBox, PageLoading } from "../components/Spinner";
import { useCurrentUser } from "../lib/auth";
import { cx } from "../lib/format";
import { AccountSettings } from "./settings/AccountSettings";
import { AgentPoolSettings } from "./settings/AgentPoolSettings";
import { ContextSettings } from "./settings/ContextSettings";
import { ContextSourceSettings } from "./settings/ContextSourceSettings";
import { EnvTemplateSettings } from "./settings/EnvTemplateSettings";
import { GeneralSettings } from "./settings/GeneralSettings";
import { SecretSettings } from "./settings/SecretSettings";
import { UsageSettings } from "./settings/UsageSettings";
import { UserSettings } from "./settings/UserSettings";
import { WorkflowSettings } from "./settings/WorkflowSettings";

interface Section {
  key: string;
  label: string;
  admin: boolean;
  group: "Project" | "Workspace" | "You";
}

const SECTIONS: Section[] = [
  { key: "general", label: "General", admin: true, group: "Project" },
  { key: "context", label: "Project context", admin: false, group: "Project" },
  { key: "workflow", label: "Workflow", admin: true, group: "Project" },
  { key: "environments", label: "Environment templates", admin: true, group: "Project" },
  { key: "context-sources", label: "Context sources", admin: false, group: "Project" },
  { key: "secrets", label: "Secrets", admin: true, group: "Project" },
  { key: "usage", label: "Usage", admin: false, group: "Project" },
  { key: "agent-pool", label: "Agent pool", admin: true, group: "Workspace" },
  { key: "users", label: "Users", admin: true, group: "Workspace" },
  { key: "account", label: "Account", admin: false, group: "You" },
];

export function SettingsPage() {
  const { pid = "", section } = useParams();
  const me = useCurrentUser();
  const project = useProject(pid);
  const [params] = useSearchParams();
  const isAdmin = me.role === "admin";
  const visible = SECTIONS.filter((s) => isAdmin || !s.admin);
  const current = visible.find((s) => s.key === section);
  if (!current) return <Navigate to={`/p/${pid}/settings/${visible[0].key}`} replace />;
  if (project.error) return <ErrorBox error={project.error} />;
  if (!project.data) return <PageLoading />;

  const groups = ["Project", "Workspace", "You"] as const;

  return (
    <div className="settings-page">
      <nav className="settings-nav" aria-label="Settings">
        {groups.map((g) => {
          const items = visible.filter((s) => s.group === g);
          if (!items.length) return null;
          return (
            <div key={g} className="settings-nav-group">
              <div className="settings-nav-heading">{g === "Project" ? project.data!.name : g}</div>
              {items.map((s) => (
                <NavLink
                  key={s.key}
                  to={`/p/${pid}/settings/${s.key}`}
                  className={({ isActive }) => cx("settings-nav-item", isActive && "active")}
                >
                  {s.label}
                </NavLink>
              ))}
            </div>
          );
        })}
      </nav>
      <div className="settings-content">
        <h1 className="page-title">{current.label}</h1>
        {params.get("created") && current.key === "general" && (
          <div className="info-box">
            Project created. Next: add an <NavLink to={`/p/${pid}/settings/environments`}>environment template</NavLink>{" "}
            and the <NavLink to={`/p/${pid}/settings/secrets`}>secrets</NavLink> your agents need.
          </div>
        )}
        {current.key === "general" && <GeneralSettings project={project.data} />}
        {current.key === "context" && <ContextSettings pid={pid} />}
        {current.key === "workflow" && <WorkflowSettings pid={pid} />}
        {current.key === "environments" && <EnvTemplateSettings pid={pid} />}
        {current.key === "context-sources" && <ContextSourceSettings pid={pid} />}
        {current.key === "secrets" && <SecretSettings pid={pid} />}
        {current.key === "usage" && <UsageSettings pid={pid} />}
        {current.key === "agent-pool" && <AgentPoolSettings />}
        {current.key === "users" && <UserSettings />}
        {current.key === "account" && <AccountSettings />}
      </div>
    </div>
  );
}
